import React, { useState, useEffect, useRef } from 'react';
import {
  INVALID_TIME_IN_MIN,
  WARNING_TIME_IN_MIN,
} from '../consts/InterviewTimeValidation';

interface EarlySubmissionModalProps {
  isOpen: boolean;
  elapsedMinutes: number;
  onConfirm: () => void;
  onCancel: () => void;
  onBackToPractice: () => void;
  onModalOpen?: () => void;
  onModalClose?: () => void;
}

const EarlySubmissionModal: React.FC<EarlySubmissionModalProps> = ({
  isOpen,
  elapsedMinutes,
  onConfirm,
  onCancel,
  onBackToPractice,
  onModalOpen,
  onModalClose,
}) => {
  const [showSecondWarning, setShowSecondWarning] = useState(false);
  const prevIsOpenRef = useRef(isOpen);

  // Handle pause/resume when modal opens/closes
  useEffect(() => {
    const prevIsOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    // Only call functions when state actually changes
    if (isOpen && !prevIsOpen) {
      // Modal just opened
      onModalOpen?.();
    } else if (!isOpen && prevIsOpen) {
      // Modal just closed
      onModalClose?.();
      // Reset second warning state when modal closes
      setShowSecondWarning(false);
    }
  }, [isOpen, onModalOpen, onModalClose]);

  // Handle cancel - useEffect will handle onModalClose when modal actually closes
  const handleCancel = () => {
    onCancel();
  };

  if (!isOpen) return null;

  // Less than 1 minute - show warning with options
  if (elapsedMinutes < INVALID_TIME_IN_MIN) {
    return (
      <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
        <div className='bg-white rounded-lg p-6 max-w-md mx-4'>
          <div className='text-center'>
            <div className='w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center'>
              <svg
                className='w-6 h-6 text-yellow-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
            </div>
            <h3 className='text-lg font-semibold text-gray-900 mb-2'>
              Interview Too Short
            </h3>
            <p className='text-gray-600 mb-4'>
              You've only been in the interview for less than a minute. Please
              take more time to work through the problem to get meaningful
              feedback.
            </p>
            <div className='bg-orange-50 border border-orange-200 rounded-lg p-3 mb-6'>
              <p className='text-orange-800 text-sm font-medium'>
                ⚠️ Warning: This interview will not be recorded or saved if you
                exit now.
              </p>
            </div>
            <div className='space-y-3'>
              <button
                onClick={handleCancel}
                className='w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={onBackToPractice}
                className='w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors'
              >
                Exit Interview
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 1-10 minutes - show warning about accuracy
  if (
    elapsedMinutes >= INVALID_TIME_IN_MIN &&
    elapsedMinutes < WARNING_TIME_IN_MIN
  ) {
    if (!showSecondWarning) {
      // First prompt - ask if they want to finish
      return (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 max-w-md mx-4'>
            <div className='text-center'>
              <div className='w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center'>
                <svg
                  className='w-6 h-6 text-yellow-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
              </div>
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                Finish Interview Early?
              </h3>
              <p className='text-gray-600 mb-6'>
                You've been working for {elapsedMinutes} minute
                {elapsedMinutes !== 1 ? 's' : ''}. Are you sure you want to
                finish the interview now?
              </p>
              <div className='space-y-3'>
                <button
                  onClick={() => setShowSecondWarning(true)}
                  className='w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors'
                >
                  Yes, Finish Interview
                </button>
                <button
                  onClick={handleCancel}
                  className='w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors'
                >
                  Continue Interview
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      // Second warning - about accuracy with options
      return (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 max-w-md mx-4'>
            <div className='text-center'>
              <div className='w-12 h-12 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center'>
                <svg
                  className='w-6 h-6 text-orange-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z'
                  />
                </svg>
              </div>
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                Results May Not Be Accurate
              </h3>
              <p className='text-gray-600 mb-6'>
                Since you've only spent {elapsedMinutes} minute
                {elapsedMinutes !== 1 ? 's' : ''} on this interview, the
                analysis and feedback may not be as comprehensive or accurate as
                a longer session.
              </p>
              <div className='space-y-3'>
                <button
                  onClick={onConfirm}
                  className='w-full bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition-colors'
                >
                  Submit Anyway
                </button>
                <button
                  onClick={onCancel}
                  className='w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors'
                >
                  Back to Interview
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  // 10+ minutes - standard confirmation
  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white rounded-lg p-6 max-w-md mx-4'>
        <div className='text-center'>
          <div className='w-12 h-12 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center'>
            <svg
              className='w-6 h-6 text-blue-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
          <h3 className='text-lg font-semibold text-gray-900 mb-2'>
            Finish Interview?
          </h3>
          <p className='text-gray-600 mb-6'>
            You've been working for {elapsedMinutes} minutes. Are you ready to
            submit your interview for analysis?
          </p>
          <div className='space-y-3'>
            <button
              onClick={onConfirm}
              className='w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors'
            >
              Submit Interview
            </button>
            <button
              onClick={handleCancel}
              className='w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors'
            >
              Continue Interview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarlySubmissionModal;
